/**
 * Products — Firestore-backed.
 *
 * Reading strategy: the active catalogue is fetched once and memoised, then
 * filtered in memory. At the current scale (tens of products) that is one small
 * read set for the entire browsing session instead of a query per filter
 * change, and it makes the catalogue's instant client-side filtering possible.
 *
 * The composite indexes for server-side filtering are already deployed
 * (`firestore.indexes.json`), so when the catalogue outgrows a single fetch the
 * per-filter functions below can be switched to real queries without a data
 * migration and without touching a single component.
 */

import { collection, doc, getDoc, query, where, orderBy, limit } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { PRODUCTS as FALLBACK_PRODUCTS, getProductBySlug as fallbackBySlug } from "@/data/products";
import { cached, safe, fetchAll, fetchFirst, mapDoc } from "./_base";

const COLLECTION = "products";

/**
 * Firestore document -> the shape the UI has always consumed.
 * The database stores explicit `*Slug` relationship fields; the UI reads
 * `brand` / `category` / `subcategory`. Mapping here keeps both honest.
 */
export const toProduct = (docData) => {
  if (!docData) return null;
  return {
    id: docData.id,
    name: docData.name || "",
    slug: docData.slug || "",
    brand: docData.brandSlug || "",
    category: docData.categorySlug || "",
    subcategory: docData.subcategorySlug || null,
    shortDescription: docData.shortDescription || "",
    description: docData.description || "",
    specifications: Array.isArray(docData.specifications) ? docData.specifications : [],
    images: Array.isArray(docData.images) ? docData.images : [],
    documents: Array.isArray(docData.documents) ? docData.documents : [],
    videos: Array.isArray(docData.videos) ? docData.videos : [],
    externalUrl: docData.externalUrl || null,
    featured: docData.featured === true,
    active: docData.active !== false,
    listingType: docData.listingType || "sale",
    // Optional per-product SEO overrides managed in the CMS.
    seo: docData.seo || null,
  };
};

const loadCatalogue = () =>
  cached("products:all", () =>
    safe(
      async () => {
        const rows = await fetchAll(
          query(
            collection(db, COLLECTION),
            where("active", "==", true),
            orderBy("sortOrder", "asc"),
            orderBy("name", "asc")
          )
        );
        return rows.map(toProduct);
      },
      FALLBACK_PRODUCTS,
      "products.getProducts"
    )
  );

const matchesQuery = (product, needle) =>
  [
    product.name,
    product.brand,
    product.category,
    product.subcategory || "",
    product.shortDescription,
    product.description,
  ]
    .join(" ")
    .toLowerCase()
    .includes(needle);

export const productsService = {
  getProducts: () => loadCatalogue(),

  /**
   * Resolve from the memoised catalogue when it is already loaded, otherwise
   * hit Firestore directly — a visitor landing straight on a product page
   * should not have to download the whole catalogue first.
   */
  getProductBySlug: async (slug) => {
    if (!slug) return null;

    return safe(
      async () => {
        const found = await fetchFirst(
          query(
            collection(db, COLLECTION),
            where("active", "==", true),
            where("slug", "==", slug),
            limit(1)
          )
        );
        return toProduct(found);
      },
      fallbackBySlug(slug) || null,
      `products.getProductBySlug(${slug})`
    );
  },

  /** Used by the CMS, which needs drafts and inactive products too. */
  getProductById: async (id) => {
    if (!id) return null;
    const snap = await getDoc(doc(db, COLLECTION, id));
    return toProduct(mapDoc(snap));
  },

  getFeaturedProducts: async () => (await loadCatalogue()).filter((p) => p.featured),

  getProductsByCategory: async (category) =>
    (await loadCatalogue()).filter((p) => p.category === category),

  getProductsByBrand: async (brand) => (await loadCatalogue()).filter((p) => p.brand === brand),

  getProductsBySubcategory: async (subcategory) =>
    (await loadCatalogue()).filter((p) => p.subcategory === subcategory),

  /** Closest first: same subcategory, then same category, then same brand. */
  getRelatedProducts: async (product, max = 4) => {
    if (!product) return [];
    const catalogue = await loadCatalogue();
    const candidates = catalogue.filter((p) => p.id !== product.id);

    const scored = candidates
      .map((p) => {
        let score = 0;
        if (product.subcategory && p.subcategory === product.subcategory) score += 4;
        if (p.category === product.category) score += 2;
        if (p.brand === product.brand) score += 1;
        return { product: p, score };
      })
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score);

    return scored.slice(0, max).map((entry) => entry.product);
  },

  /**
   * Substring search across the cached catalogue.
   *
   * Firestore has no native full-text search. At this catalogue size an
   * in-memory scan is instant and free; the upgrade path when that stops being
   * true is an Algolia / Typesense index fed by a Firestore trigger.
   */
  search: async (queryString) => {
    const needle = (queryString || "").trim().toLowerCase();
    if (!needle) return [];
    const catalogue = await loadCatalogue();
    return catalogue.filter((product) => matchesQuery(product, needle));
  },
};

export default productsService;
